import table from './aisles.json';
import { normaliseItemName } from './normaliseItemName';
import { OTHER, isAisle, type Aisle } from './aisleLabels';

/**
 * Which aisle a name belongs in.
 *
 * **This is the whole classifier**, and it is a lookup. The model that decided any of it
 * ran once, on a dev machine, and wrote `aisles.json` — see `scripts/generate-aisles.mjs`
 * for why it is not in the browser. What arrives on the phone is a few hundred words and
 * the thirty lines below, which is why a row can be filed the instant it is typed, offline,
 * on the first launch, with nothing downloaded and nothing to warm up.
 *
 * Everything here is pure and DOM-free on purpose, like `imagePlan.ts`: it is the part
 * worth having tests for, and none of it should need a browser to run.
 */

/**
 * Tokens that say how much rather than what. Dropped before matching, so "2 pints milk",
 * "milk 2l" and "milk" are one question.
 */
const UNITS = new Set([
  'x',
  'kg',
  'g',
  'mg',
  'l',
  'ml',
  'cl',
  'dag',
  'pint',
  'pints',
  'litre',
  'litres',
  'liter',
  'liters',
  'pack',
  'packs',
  'tin',
  'tins',
  'jar',
  'jars',
  'bottle',
  'bottles',
  'box',
  'bag',
  'szt',
  'opak',
  'op',
  'kpl',
]);

/**
 * A number, optionally with a unit stuck to it: "2", "500g", "1.5l", "2x".
 *
 * Anchored at both ends so a name that merely starts with a digit survives — "7up" is a
 * drink, not a quantity, and a looser test would throw the only word away.
 */
const QUANTITY = /^\d+(?:[.,]\d+)?(?:x|kg|g|mg|l|ml|cl|dag|szt)?$/;

/** The shipped table, flattened once at module load. */
const LOOKUP = buildLookup();

function buildLookup(): Map<string, Aisle> {
  const lookup = new Map<string, Aisle>();

  for (const [aisle, terms] of Object.entries(table.terms)) {
    if (!isAisle(aisle)) {
      continue;
    }

    for (const term of terms.split(',')) {
      if (term) {
        lookup.set(term, aisle);
      }
    }
  }

  return lookup;
}

/** How many distinct names the shipped table knows. Exported for the tests to hold it to. */
export const knownTermCount = LOOKUP.size;

function meaningfulWords(name: string): string[] {
  return normaliseItemName(name)
    .split(' ')
    .filter((word) => word.length > 0 && !QUANTITY.test(word) && !UNITS.has(word));
}

/**
 * The aisle the shipped table puts this name in, or `other`.
 *
 * Three passes, cheapest first:
 *
 * 1. the name itself, normalised — which is what nearly every row is;
 * 2. the name with the quantities taken out, so "2 pints milk" is the milk question;
 * 3. the longest run of consecutive words inside it that the table knows, so "organic
 *    whole milk" finds `milk` and "tesco frozen peas" finds `frozen peas` rather than
 *    `peas`.
 *
 * Longest-run-wins is what makes a few hundred terms cover far more than a few hundred
 * names, and it is why the seed files prefer the bare noun to the branded phrase: the
 * short term is findable inside the long one, and never the other way round.
 */
export function aisleFor(name: string): Aisle {
  const exact = LOOKUP.get(normaliseItemName(name));

  if (exact) {
    return exact;
  }

  const words = meaningfulWords(name);

  if (words.length === 0) {
    return OTHER;
  }

  const withoutQuantities = LOOKUP.get(words.join(' '));

  if (withoutQuantities) {
    return withoutQuantities;
  }

  for (let length = words.length - 1; length >= 1; length -= 1) {
    for (let start = 0; start + length <= words.length; start += 1) {
      const run = LOOKUP.get(words.slice(start, start + length).join(' '));

      if (run) {
        return run;
      }
    }
  }

  return OTHER;
}

/**
 * The aisle to actually draw this name under: the household's correction if it has made
 * one, otherwise the shipped table.
 *
 * The corrections are keyed by *normalised* name, because that is what the server files
 * them under — the same key the chips and the photos use, so "Oat Milk" and "oat milk" are
 * one thing here exactly as they are one thing everywhere else.
 */
export function resolveAisle(
  name: string,
  corrections: ReadonlyMap<string, Aisle> | undefined,
): Aisle {
  return corrections?.get(normaliseItemName(name)) ?? aisleFor(name);
}
